/**
 * The firm's clients as combobox options for the firm calendar's editor.
 * One cached, persisted query; the list is small enough to fetch whole.
 */
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { ForeignNationalsAPI } from "@/api/foreignNationals";
import { queryClient } from "@/lib/query/queryClient";

export const FIRM_CLIENTS_LIMIT = 1000;
export const firmClientsKey = ["firm-clients"] as const;

export type ClientOption = { value: string; label: string };

export const useFirmClients = (): {
  options: ClientOption[];
  isLoading: boolean;
  error: string | null;
} => {
  const query = useQuery(
    {
      queryKey: firmClientsKey,
      queryFn: () =>
        ForeignNationalsAPI.list({
          limit: FIRM_CLIENTS_LIMIT,
          sort: "firstNameAsc"
        }),
      staleTime: 60_000,
      meta: { persist: true }
    },
    queryClient
  );
  const options = useMemo<ClientOption[]>(
    () =>
      (query.data?.foreign_nationals ?? [])
        .map((fn) => ({
          value: fn.uid,
          label:
            [fn.first_name, fn.last_name].filter(Boolean).join(" ").trim() ||
            fn.email ||
            fn.uid
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [query.data]
  );
  return {
    options,
    isLoading: query.isPending && !query.data,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : "Couldn't load clients."
      : null
  };
};
